// ts-check
import React, { useCallback, useState, useMemo } from 'react';
import { Button, Flex, FlexItem, Stack, StackItem, Switch, FormGroup } from '@patternfly/react-core';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import T from 'localization/LocalizationKeys';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import SimpleTable, { createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';

import DeleteProvider from './DeleteProvider';
import DeleteProviderClient from './DeleteProviderClient';
import { useTranslation } from 'localization/useTranslation';
import ModelType from 'components/Model/ModelType';
import ModelsContainer from 'components/Model/ModelsContainer';
import Tl from 'localization/LocalizationKeys';
import useAlerts from 'utils/hooks/useAlerts';

import './ProviderPage.css';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import { useProvider } from './queries';

const model = [
  {
    type: ModelType.TEXT,
    label: T.model.id,
    path: 'id',
    isReadOnly: true
  },
  {
    type: ModelType.TEXT,
    label: T.model.version,
    path: 'type',
    isReadOnly: true
  }
];

const columns = [
  {
    label: Tl.common.name,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: Tl.common.enabled,
    path: '.',
    isSortable: true,
    render: (item) => <Switch key={item.id} aria-label={item.displayName} value={item.enabled} />
  },
  {
    label: Tl.common.description,
    path: 'description',
    isSortable: true
  }
];

export const ProviderName = ({ providerId }) => {
  const { provider, isLoading } = useProvider(providerId);
  return isLoading ? <BreadcrumbSkeleton /> : (provider?.displayName ?? '');
};

const AdminProvider = ({ breadcrumbs }) => {
  const history = useHistory();
  const location = useLocation();
  const T = useTranslation();
  const { providerId } = useParams();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [alerts, addSuccessAlert] = useAlerts();

  const { provider, isLoading, providerClients, isLoadingProviderClients } = useProvider(providerId, true);

  const onViewProviderClient = useCallback((providerClient) => {
    history.push({
      pathname: `${providerId}/clients/${providerClient.id}`,
      search: location.search
    });
  }, [history, location.search, providerId]);


  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const onProviderDeleteSuccess = useCallback(() => {
    history.push('/admin/providers');
  }, [history]);

  const deletedProviderClient = useCallback(() => {
    setConfirmDeleteItem(null);
    addSuccessAlert(T.common.request.delete.success);
  }, [T.common.request.delete.success, addSuccessAlert]);

  const onDeleteProviderClient = useCallback(providerClient => {
    setConfirmDeleteItem({ providerId, providerClient });
  }, [providerId]);
  const actions = useMemo(() => {
    return [
      ...createDefaultActions({ onDelete: onDeleteProviderClient })
    ];
  }, [onDeleteProviderClient]);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/providers/view'}>
      { alerts }
      <Stack>
        <StackItem>
          <Flex>
            <FlexItem>
              <ModelsContainer models={model} isLoading={isLoading} data={provider} setData={() => {}}/>
            </FlexItem>
            <FlexItem>
              <FormGroup
                label={T.common.actions}
              >
                <Flex direction={{ default: 'column' }} >
                  <FlexItem>
                    <Button variant="secondary" onClick={() => {
                      history.push(`${location.pathname}/templates`);
                    }}>{ T.common.templates }</Button>
                  </FlexItem>
                  <FlexItem>
                    <Button isDisabled variant="danger" onClick={() => setConfirmDeleteItem(provider)}>{ T.common.delete }</Button>
                  </FlexItem>
                </Flex>
              </FormGroup>
            </FlexItem>
            <FlexItem />
          </Flex>
        </StackItem>
        <StackItem>
          <Flex />
        </StackItem>
        <StackItem className={'view-container'}>
          <SimpleTable tabClassName={'view-content'}
            isLoading={isLoadingProviderClients}
            onRowClick={onViewProviderClient}
            data={providerClients}
            columns={columns}
            actions={actions}
            compact
            usePaging
          />
        </StackItem>
      </Stack>
      <DeleteProvider
        confirmDeleteItem={confirmDeleteItem}
        onDelete={onProviderDeleteSuccess}
        cancelDelete={cancelDelete}
      />
      <DeleteProviderClient
        providerId={providerId}
        confirmDeleteItem={confirmDeleteItem}
        onDelete={deletedProviderClient}
        cancelDelete={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminProvider;
